import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  TextField,
  Card,
  Box,
  Switch,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";
import {
  useText,
  useGetData,
  usePutData,
  usePostData,
  useGetList,
} from "../../hooks";
import useStore from "../../context";
import * as Api from "../../service";
import { Save, Select, MultiSelect } from "../../components";
import { useParams } from "react-router-dom";
import { useTranslation } from "localization/translation";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";

const DISCOUNT_TYPES = {
  PERCENTAGE: "percentage",
  FIXED_PRICE: "fixed_price",
  EXTRA_DAYS: "extra_days",
};

const BILLING_PERIODS = {
  MONTHLY: "monthly",
  ANNUAL: "annual",
  BOTH: "both",
};

const BILLING_PERIOD_OPTIONS = [
  { id: BILLING_PERIODS.MONTHLY, name: "monthly" },
  { id: BILLING_PERIODS.ANNUAL, name: "annual" },
  { id: BILLING_PERIODS.BOTH, name: "billing_period_both" },
];

const initForm = {
  code: "",
  name: "",
  type: DISCOUNT_TYPES.PERCENTAGE,
  value: "",
  is_active: true,
  valid_from: null,
  valid_until: null,
  usage_starts_at: null,
  usage_ends_at: null,
  prices: {},
  plan_ids: [],
  plan_periods: {},
  country_id: "",
  source: "admin",
  is_system_generated: false,
  user_id: null,
  allow_at_registration: false,
};

const COUNTRIES_QUERY = {
  sort: "name_en",
  order: "asc",
};

const Form = ({ variant = "promo" }) => {
  const isAmbassador = variant === "ambassador";
  const codesApi = isAmbassador ? Api.ambassadorCodes : Api.promoCodes;
  const { id } = useParams();
  const [data, , refresh] = useGetData(id && codesApi.getById, id);
  const postCallback = usePostData(codesApi.create, refresh);
  const updateCallback = usePutData(codesApi.update, refresh);
  const { setErrorMsg, language, lang } = useStore();
  const translation = useTranslation();
  const [currencies, setCurrencies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [countries] = useGetList(
    isAmbassador ? Api.countries.getAll : null,
    COUNTRIES_QUERY
  );

  useEffect(() => {
    Api.main
      .getPlans()
      .then((res) => {
        setCurrencies(res?.data?.currencies || res?.currencies || []);
        setPlans(res?.data?.plans || res?.plans || []);
      })
      .catch(() => {
        setCurrencies([]);
        setPlans([]);
      });
  }, []);

  const formData = useMemo(() => {
    if (!data) {
      return {
        ...initForm,
        value: isAmbassador ? "0" : "",
      };
    }
    const pricesMap = {};
    (data.prices || []).forEach((p) => {
      pricesMap[p.currency_id] = p.price;
    });
    const linked = data.plans || [];
    const planIds = linked.length
      ? linked.map((p) => Number(p.plan_id))
      : (data.plan_ids || []).map(Number);
    const planPeriods = {};
    linked.forEach((p) => {
      planPeriods[p.plan_id] = p.billing_period || BILLING_PERIODS.BOTH;
    });
    planIds.forEach((planId) => {
      if (!planPeriods[planId]) planPeriods[planId] = BILLING_PERIODS.BOTH;
    });
    return {
      code: data.code || "",
      name: data.name || "",
      type: data.type || DISCOUNT_TYPES.PERCENTAGE,
      value: data.value != null ? String(data.value) : "",
      is_active: !!data.is_active,
      valid_from: data.valid_from || data.start_date || null,
      valid_until: data.valid_until || data.end_date || null,
      usage_starts_at: data.usage_starts_at || data.usage_start_date || null,
      usage_ends_at: data.usage_ends_at || data.usage_end_date || null,
      prices: pricesMap,
      plan_ids: planIds,
      plan_periods: planPeriods,
      country_id: data.country_id || data.country?.id || "",
      source: data.source || "admin",
      is_system_generated: !!data.is_system_generated || data.source === "auto",
      user_id: data.user_id != null ? Number(data.user_id) : null,
      allow_at_registration: !!data.allow_at_registration,
    };
  }, [data, isAmbassador]);

  const [text, setText, , input] = useText(formData);
  const planNameField = `name_${language || "en"}`;
  const countryNameField = `name_${lang || language || "en"}`;
  const isSystemPromo =
    !isAmbassador &&
    (!!text.is_system_generated ||
      text.source === "auto" ||
      text.user_id != null);

  const typeOptions = isAmbassador
    ? [
        { id: DISCOUNT_TYPES.PERCENTAGE, name: "discount_type_percentage" },
        { id: DISCOUNT_TYPES.FIXED_PRICE, name: "discount_type_fixed_price" },
      ]
    : [
        { id: DISCOUNT_TYPES.PERCENTAGE, name: "discount_type_percentage" },
        { id: DISCOUNT_TYPES.FIXED_PRICE, name: "discount_type_fixed_price" },
        { id: DISCOUNT_TYPES.EXTRA_DAYS, name: "discount_type_extra_days" },
      ];

  const handlePlansChange = (ids) => {
    setText((st) => {
      const nextIds = (ids || []).map(Number);
      const nextPeriods = { ...(st.plan_periods || {}) };
      nextIds.forEach((planId) => {
        if (!nextPeriods[planId]) {
          nextPeriods[planId] = BILLING_PERIODS.BOTH;
        }
      });
      Object.keys(nextPeriods).forEach((key) => {
        if (!nextIds.includes(Number(key))) {
          delete nextPeriods[key];
        }
      });
      return { ...st, plan_ids: nextIds, plan_periods: nextPeriods };
    });
  };

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault();

      const code = String(text.code || "").trim();
      const name = String(text.name || "").trim();
      const type = text.type || DISCOUNT_TYPES.PERCENTAGE;
      const planIds = (text.plan_ids || []).map(Number).filter(Boolean);
      const plansRequired = type !== DISCOUNT_TYPES.EXTRA_DAYS;

      if (!code || !name) {
        setErrorMsg(translation.fill_required_fields);
        return;
      }

      if (!isSystemPromo) {
        const codeValid = isAmbassador
          ? /^[A-Za-z]+[A-Za-z0-9]*$/.test(code)
          : /^BC_.+/i.test(code);
        if (!codeValid) {
          setErrorMsg(
            isAmbassador
              ? translation.invalid_ambassador_code_format
              : translation.invalid_promo_code_format
          );
          return;
        }
      }

      if (isAmbassador && !text.country_id) {
        setErrorMsg(translation.ambassador_country_required);
        return;
      }

      if (plansRequired && !planIds.length) {
        setErrorMsg(translation.promo_plans_required);
        return;
      }

      const payload = {
        code,
        name,
        type,
        is_active: !!text.is_active,
        valid_from: text.valid_from
          ? moment(text.valid_from).startOf("day").toISOString()
          : null,
        valid_until: text.valid_until
          ? moment(text.valid_until).endOf("day").toISOString()
          : null,
        usage_starts_at: text.usage_starts_at
          ? moment(text.usage_starts_at).startOf("day").toISOString()
          : null,
        usage_ends_at: text.usage_ends_at
          ? moment(text.usage_ends_at).endOf("day").toISOString()
          : null,
        allow_at_registration:
          type === DISCOUNT_TYPES.EXTRA_DAYS
            ? !!text.allow_at_registration && !isSystemPromo
            : false,
        plans: planIds.map((planId) => ({
          plan_id: planId,
          billing_period:
            text.plan_periods?.[planId] || BILLING_PERIODS.BOTH,
        })),
      };

      if (isAmbassador) {
        payload.country_id = Number(text.country_id);
      }

      if (type === DISCOUNT_TYPES.PERCENTAGE) {
        const rawValue = text.value;
        const value =
          isAmbassador && (rawValue === "" || rawValue == null)
            ? 0
            : Number(rawValue);
        if (!Number.isFinite(value) || value < 0 || value > 100) {
          setErrorMsg(translation.invalid_percentage);
          return;
        }
        payload.value = value;
        payload.prices = [];
      } else if (type === DISCOUNT_TYPES.FIXED_PRICE) {
        const prices = currencies
          .map((c) => ({
            currency_id: Number(c.id),
            price: Number(text.prices?.[c.id]),
          }))
          .filter((p) => Number.isFinite(p.price) && p.price >= 0);

        if (!prices.length) {
          setErrorMsg(translation.fixed_price_required);
          return;
        }
        payload.value = null;
        payload.prices = prices;
      } else if (type === DISCOUNT_TYPES.EXTRA_DAYS) {
        const value = Number(text.value);
        if (!Number.isFinite(value) || value <= 0 || !Number.isInteger(value)) {
          setErrorMsg(translation.invalid_extra_days);
          return;
        }
        payload.value = value;
        payload.prices = [];
      }

      if (id) {
        updateCallback({ ...payload, id: Number(id) });
      } else {
        postCallback(payload);
      }
    },
    [
      currencies,
      id,
      isAmbassador,
      isSystemPromo,
      postCallback,
      setErrorMsg,
      text,
      translation,
      updateCallback,
    ]
  );

  return (
    <Container>
      <Card className="shadow-xxl px-4 py-2">
        <Box component="form" onSubmit={submitForm}>
          {isSystemPromo && (
            <Typography variant="body2" color="textSecondary" className="mb-3">
              {translation.promo_system_generated_hint}
            </Typography>
          )}

          <FormControlLabel
            control={
              <div className="m-2">
                <Switch
                  onClick={() =>
                    setText((st) => ({ ...st, is_active: !st.is_active }))
                  }
                  checked={!!text.is_active}
                  className="switch-medium toggle-switch-first"
                />
              </div>
            }
            label={translation.enable_disable}
          />
          <br />
          <br />

          <TextField
            {...input("code")}
            label={translation.code}
            disabled={isSystemPromo}
            helperText={
              isAmbassador
                ? translation.ambassador_code_format_hint
                : translation.promo_code_format_hint
            }
          />
          <TextField
            {...input("name")}
            label={translation.name}
            disabled={isSystemPromo && text.type !== DISCOUNT_TYPES.EXTRA_DAYS}
          />

          {!isAmbassador && (
            <TextField
              label={translation.promo_source}
              value={
                isSystemPromo
                  ? translation.promo_source_auto
                  : translation.promo_source_admin
              }
              disabled
              variant="outlined"
              className="my-2"
              fullWidth
            />
          )}

          {isAmbassador && !!countries?.length && (
            <Select
              onChange={(val) =>
                setText((st) => ({ ...st, country_id: val }))
              }
              value={text.country_id || ""}
              field={countryNameField}
              label={`${translation.country}*`}
              options={countries}
              noCard
            />
          )}

          <Select
            value={text.type || DISCOUNT_TYPES.PERCENTAGE}
            onChange={(val) => setText((st) => ({ ...st, type: val }))}
            field="name"
            label={translation.discount_type}
            options={typeOptions}
            isFieldTranslate
            disabled={isSystemPromo}
          />

          {text.type === DISCOUNT_TYPES.PERCENTAGE && (
            <TextField
              {...input("value")}
              label={translation.discount_percentage}
              type="number"
              inputProps={{ min: 0, max: 100, step: "0.01" }}
            />
          )}

          {text.type === DISCOUNT_TYPES.EXTRA_DAYS && (
            <>
              <TextField
                {...input("value")}
                label={translation.discount_extra_days}
                type="number"
                inputProps={{ min: 1, step: "1" }}
              />
              <FormControlLabel
                control={
                  <div className="m-2">
                    <Switch
                      disabled={isSystemPromo}
                      onClick={() =>
                        setText((st) => ({
                          ...st,
                          allow_at_registration: !st.allow_at_registration,
                        }))
                      }
                      checked={!!text.allow_at_registration && !isSystemPromo}
                      className="switch-medium toggle-switch-first"
                    />
                  </div>
                }
                label={translation.promo_allow_at_registration}
              />
            </>
          )}

          {text.type === DISCOUNT_TYPES.FIXED_PRICE &&
            currencies.map((currency) => (
              <TextField
                key={currency.id}
                variant="outlined"
                className="my-2"
                fullWidth
                required
                type="number"
                label={`${translation.discount_fixed_price} (${currency.code})`}
                value={text.prices?.[currency.id] ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setText((st) => ({
                    ...st,
                    prices: {
                      ...(st.prices || {}),
                      [currency.id]: value,
                    },
                  }));
                }}
                inputProps={{ min: 0, step: "0.01" }}
                disabled={isSystemPromo}
              />
            ))}

          {!isSystemPromo && !!plans.length && (
            <MultiSelect
              field={planNameField}
              label={
                text.type === DISCOUNT_TYPES.EXTRA_DAYS
                  ? `${translation.plans} (${translation.promo_plans_optional_extra_days})`
                  : `${translation.plans}*`
              }
              data={plans}
              value={text.plan_ids || []}
              onChange={handlePlansChange}
            />
          )}

          {!isSystemPromo &&
            (text.plan_ids || []).map((planId) => {
              const plan = plans.find((p) => Number(p.id) === Number(planId));
              const planName =
                plan?.[planNameField] || plan?.name_en || `#${planId}`;
              return (
                <Select
                  key={planId}
                  value={
                    text.plan_periods?.[planId] || BILLING_PERIODS.BOTH
                  }
                  onChange={(val) =>
                    setText((st) => ({
                      ...st,
                      plan_periods: {
                        ...(st.plan_periods || {}),
                        [planId]: val,
                      },
                    }))
                  }
                  field="name"
                  label={`${translation.plan_billing_period}: ${planName}`}
                  options={BILLING_PERIOD_OPTIONS}
                  isFieldTranslate
                />
              );
            })}

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              label={translation.valid_from}
              variant="inline"
              autoOk
              format="dd.MM.yyyy"
              disableToolbar
              clearable
              value={text.valid_from ? new Date(text.valid_from) : null}
              onChange={(val) =>
                setText((st) => ({ ...st, valid_from: val }))
              }
              style={{ width: "100%", padding: "8px" }}
            />
            <DatePicker
              label={translation.valid_until}
              variant="inline"
              autoOk
              format="dd.MM.yyyy"
              disableToolbar
              clearable
              value={text.valid_until ? new Date(text.valid_until) : null}
              onChange={(val) =>
                setText((st) => ({ ...st, valid_until: val }))
              }
              style={{ width: "100%", padding: "8px" }}
            />
            {!isAmbassador && (
              <>
                <DatePicker
                  label={translation.usage_starts_at}
                  variant="inline"
                  autoOk
                  format="dd.MM.yyyy"
                  disableToolbar
                  clearable
                  value={
                    text.usage_starts_at
                      ? new Date(text.usage_starts_at)
                      : null
                  }
                  onChange={(val) =>
                    setText((st) => ({ ...st, usage_starts_at: val }))
                  }
                  style={{ width: "100%", padding: "8px" }}
                />
                <DatePicker
                  label={translation.usage_ends_at}
                  variant="inline"
                  autoOk
                  format="dd.MM.yyyy"
                  disableToolbar
                  clearable
                  value={
                    text.usage_ends_at ? new Date(text.usage_ends_at) : null
                  }
                  onChange={(val) =>
                    setText((st) => ({ ...st, usage_ends_at: val }))
                  }
                  style={{ width: "100%", padding: "8px" }}
                />
              </>
            )}
          </MuiPickersUtilsProvider>

          <Save />
        </Box>
      </Card>
    </Container>
  );
};

export default Form;
